import { FileUploadOutlined } from '@mui/icons-material';
import UploadIcon from '@mui/icons-material/Upload';
import { Menu, MenuItem, Typography } from '@mui/material';
import { useAtomValue } from 'jotai';
import { type ChangeEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useImportFile } from '@/api/mutations/useImportFile';
import { generateResumeInProgressAtom } from '@/atoms/generators';
import { importInProgressAtom } from '@/atoms/import';
import { AdvancedImportDialog, type AdvancedImportPayload } from '@/components/AdvancedImportDialog/AdvancedImportDialog';
import { Text } from '@/components/ui/text';
import { Tooltip } from '@/components/ui/tooltip';
import { snackbarColor } from '@/enums';
import { useProjectId, useSnackbar } from '@/hooks';
import { createImportSseHandlers } from '@/hooks/useResumeImportSse';
import { usePrefValue } from '@/preferences';
import { ImportButton } from './ImportScript.styles';

export const ImportScriptButton = () => {
    const { t } = useTranslation();
    const side_bar = usePrefValue('side_bar');
    const projectId = useProjectId();
    const importFile = useImportFile();
    const showSnackbar = useSnackbar();
    const anyImportInProgress = useAtomValue(importInProgressAtom);
    const generateResumeInProgress = useAtomValue(generateResumeInProgressAtom);
    const [menuOpen, setMenuOpen] = useState(false);
    const [advancedImportOpen, setAdvancedImportOpen] = useState(false);
    const anchorRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Tab-wide — any project importing blocks a new one, since this client
    // only tracks a single import marker at a time. Import and generation
    // share one SSE channel with no job type on the wire, so either one's
    // 100% would read as the other's completion — never let them overlap.
    const busy = importFile.isPending || anyImportInProgress || generateResumeInProgress;

    const runImport = (file: File, prompts?: Omit<AdvancedImportPayload, 'file'>) => {
        if (!projectId) return;

        showSnackbar({ open: true, message: `${t('import_doc')}...`, severity: snackbarColor.success, progress: undefined });

        importFile.mutate({
            project_id: projectId,
            file,
            actPrompt: prompts?.actPrompt,
            episodePrompt: prompts?.episodePrompt,
            scenePrompt: prompts?.scenePrompt,
            genericLogline: prompts?.genericLogline,
            genericSynopsis: prompts?.genericSynopsis,
            outlineOnly: prompts?.outlineOnly,
            ...createImportSseHandlers({ showSnackbar, t }),
        });
    };

    const handleImport = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setMenuOpen(false);
        runImport(file);
        // Reset input so the same file can be re-selected
        e.target.value = '';
    };

    const handleAdvancedImport = ({ file, ...prompts }: AdvancedImportPayload) => {
        setAdvancedImportOpen(false);
        runImport(file, prompts);
    };

    return (
        <>
            <ImportButton ref={anchorRef} sideBar={side_bar} onClick={() => setMenuOpen(true)}>
                {side_bar ? (
                    <>
                        <UploadIcon fontSize="small" />
                        <Text variant="body2" color="text-inherit" children={t('import.import-script')} />
                    </>
                ) : (
                    <Tooltip
                        title={<Typography variant="body2" color={theme => theme.custom.c22} children={t('import.import-script')} />}
                        disableInteractive
                        placement="left"
                        sideOffset={8}
                        contentClassName="py-2 pr-3 pl-0 bg-bg-paper"
                    >
                        <div className="flex">
                            <UploadIcon fontSize="small" />
                        </div>
                    </Tooltip>
                )}
            </ImportButton>
            <Menu
                anchorEl={anchorRef.current}
                open={menuOpen}
                onClose={() => setMenuOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                slotProps={{ paper: { className: 'bg-bg-paper' } }}
            >
                <MenuItem onClick={() => inputRef.current?.click()} disabled={busy}>
                    <FileUploadOutlined fontSize="small" className="mr-2" />
                    <Text variant="body1" color="text-text-primary">
                        {t('import_doc')}
                    </Text>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        setMenuOpen(false);
                        setAdvancedImportOpen(true);
                    }}
                    disabled={busy}
                >
                    <FileUploadOutlined fontSize="small" className="mr-2" />
                    <Text variant="body1" color="text-text-primary">
                        {t('import.advanced.menu')}
                    </Text>
                </MenuItem>
            </Menu>
            <input ref={inputRef} type="file" accept=".docx" hidden onChange={handleImport} />
            <AdvancedImportDialog
                open={advancedImportOpen}
                isPending={busy}
                onClose={() => setAdvancedImportOpen(false)}
                onSubmit={handleAdvancedImport}
            />
        </>
    );
};
